import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface FormFieldProps extends React.ComponentProps<typeof Input> {
  label: string;
  error?: string;
  description?: string;
  required?: boolean;
}

export function FormField({ 
  label, 
  error, 
  description, 
  required, 
  className,
  id,
  ...props 
}: FormFieldProps) {
  const fieldId = id || label.toLowerCase().replace(/\s+/g, '-');
  const errorId = `${fieldId}-error`;
  const descriptionId = `${fieldId}-description`;

  return (
    <div className="space-y-2">
      <Label 
        htmlFor={fieldId} 
        className={cn(
          "text-sm font-medium",
          error && "text-destructive",
          required && "after:content-['*'] after:ml-0.5 after:text-destructive"
        )}
      >
        {label}
      </Label>
      
      <Input
        id={fieldId}
        className={cn(
          "min-h-11 transition-colors",
          error && "border-destructive focus-visible:ring-destructive",
          className
        )}
        aria-invalid={!!error}
        aria-describedby={cn(
          description && descriptionId,
          error && errorId
        )}
        {...props}
      />
      
      {description && (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      
      {error && (
        <p 
          id={errorId} 
          className="text-xs text-destructive font-medium"
          role="alert"
          aria-live="polite"
        >
          {error}
        </p>
      )}
    </div>
  );
}